import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  BeWizrCourse,
  BeWizrEmbedItem,
  BeWizrLearningTrack,
  BeWizrLibraryItem,
  BeWizrLibraryItemType,
  BeWizrLinkItem,
  BeWizrMediaItem,
  BeWizrMeeting,
} from '~/models'
import { memo } from '~/ui/component'
import { HBox, Label, ListBar, SVG } from '~/ui/components'
import { layout } from '~/ui/styling'
import { register } from './registry'

export interface Props {
  item: BeWizrLibraryItem
}

const BeWizrLibraryItemSearchResult = memo('BeWizrLibraryItemSearchResult', (props: Props) => {

  const {item} = props

  const [t] = useTranslation('bewizr-library')

  const model = item.item

  const icon = React.useMemo(
    () => BeWizrLibraryItemType.iconForType(item.type),
    [item.type],
  )

  const image = React.useMemo(() => {
    const isMiscItem = model instanceof BeWizrEmbedItem || model instanceof BeWizrLinkItem || model instanceof BeWizrMediaItem
    if (model.links.image != null) {
      return model.links.image
    } else if (isMiscItem && model.type === 'embeditem') {
      return model.embedJson.thumbnailUrl
    } else if (isMiscItem && model.type === 'linkitem') {
      return model.openGraphJson?.image
    } else if (isMiscItem && model.type === 'mediaitem') {
      return model.images[0]
    }
  }, [model])

  const href = React.useMemo(() => {
    if (model instanceof BeWizrCourse) {
      return `//bewizr-course/${model.slug}`
    } else if (model instanceof BeWizrLearningTrack) {
      return `//bewizr-learning-track/${model.id}`
    } else if (model instanceof BeWizrMeeting) {
      const occurrenceID = model.occurrences.length === 1 ? model.occurrences[0].id : null
      return `//bewizr-meeting/${model.id}${occurrenceID != null ? `/occurrences/${occurrenceID}` : ''}`
    } else if (model instanceof BeWizrLinkItem) {
      return `//bewizr-misc-item/link/${model.id}`
    } else if (model instanceof BeWizrEmbedItem) {
      return `//bewizr-misc-item/embed/${model.id}`
    } else if (model instanceof BeWizrMediaItem) {
      return `//bewizr-misc-item/media/${model.id}`
    }
  }, [model])

  //------
  // Rendering

  function render() {
    return (
      <ListBar
        image={image == null ? undefined : {type: 'remote', url: image}}
        caption={model.title}
        detail={renderSubtitle()}
        accessory={<SVG name='chevron-right' size={layout.icon.m} dimmer/>}
        href={href}
      />
    )
  }

  function renderSubtitle() {
    return (
      <HBox gap={layout.padding.inline.s}>
        <SVG name={icon} size={layout.icon.s} dim/>
        <Label small dim>
          {model.shortDescription != null ? model.shortDescription : t(`item-types.${item.type}`)}
        </Label>
      </HBox>
    )
  }

  return render()

})

register('bewizr-library-items', BeWizrLibraryItemSearchResult)